import React, { useContext, useEffect, useState, useMemo } from "react";
import { Button, Col, Form, FormControl, InputGroup, Modal, Row, Table, Accordion, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { GlobalContext } from "../Context/globalContext";
import FirebaseApp from '../firebase';
import Swal from "sweetalert2";
import { ref, get, set, onValue } from 'firebase/database';
import { BsSpeedometer2, BsPhone, BsFileEarmarkBarGraph, BsPencilSquare } from "react-icons/bs";

const PROGRAM_LIST = [
    "anxiety",
    "depression",
    "fatigue",
    "sleeplessness",
    "stress",
    "trauma",
    "examAnxiety",
    "sports",
];
const PROGRAM_SELECTION_WITH_ALL = ["all", ...PROGRAM_LIST];
const RESEARCH_TYPES = ["phq9", "pcl5", "olbi", "isi", "gad7"];
const NOTIFICATION_LANGUAGES = ["en", "nl", "he"];

const getDefaultNotification = () => {
    return NOTIFICATION_LANGUAGES.reduce((acc, lang) => {
        acc[lang] = { title: "", description: "" };
        return acc;
    }, {});
};
const LANGUAGE_LABELS = {
    en: "English",
    nl: "Dutch",
    he: "Hebrew",
};

function capitalizeWords(str) {
    return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export const Companies = () => {

    const getDefaultResearchFields = () => ({
        researchEnabled: false,
        researchType: "",
        researchMinDaysSinceStart: 0,
        researchMinCompletedSessions: 0,
        researchRequired: false,
        researchFollowUp: false,
        researchNotificationTime: "",
    });

    const globalContext = useContext(GlobalContext);
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [salesManagers, setSalesManagers] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState({ created_by: globalContext.userEmail, availablePrograms: ["all"], referral_discount: false, ...getDefaultResearchFields(), notification: getDefaultNotification() });
    const [totalLicenses, setTotalLicenses] = useState(0);
    const [showEditModal, setShowEditModal] = useState(false);
    const [search, setSearch] = useState('');
    const [addModal, setAddModal] = useState(false);
    const [addManager, setAddManager] = useState(false);
    const [selectedSalesManager, setSelectedSalesManager] = useState("");
    const [salesManagerSearch, setSalesManagerSearch] = useState('');
    const [managerCompanySearches, setManagerCompanySearches] = useState({});
    const [activeAccordionKey, setActiveAccordionKey] = useState(null);
    const [selectedPrograms, setSelectedPrograms] = useState([...PROGRAM_SELECTION_WITH_ALL]);
    const [researchTypes, setResearchTypes] = useState([]);
    const [loadingResearchTypes, setLoadingResearchTypes] = useState(true);
    const [initialResearchType, setInitialResearchType] = useState("");
    const [viewMode, setViewMode] = useState("basic");

    const isSuperAdmin = useMemo(() => {
        return globalContext.userRole === 'SuperAdmin';
    }, [globalContext.userRole]);

    const addNewCompany = () => {
        setAddModal(true);
        setSelectedCompany({
            created_by: globalContext.userEmail,
            availablePrograms: ["all"],
            referral_discount: false,
            ...getDefaultResearchFields(),
            notification: getDefaultNotification(),
        });
        setInitialResearchType("");
        setSelectedPrograms([...PROGRAM_SELECTION_WITH_ALL]);
    }

    const addNewSalesManager = () => {
        setAddManager(true);
        setSelectedSalesManager("");
    }

    useEffect(() => {
        if (isSuperAdmin) {
            fetchSalesManagers();
        } else {
            fetchAllRelatedCompanies();
        }
    }, [isSuperAdmin]);

    useEffect(() => {
        const storedKey = localStorage.getItem('companies-active-key');
        if (storedKey) {
            setActiveAccordionKey(storedKey);
        }
    }, []);

    const handleAccordionSelect = (eventKey) => {
        const nextKey = activeAccordionKey === eventKey ? null : eventKey;
        setActiveAccordionKey(nextKey);
        if (nextKey) {
            localStorage.setItem('companies-active-key', nextKey);
        } else {
            localStorage.removeItem('companies-active-key');
        }
    };

    useEffect(() => {
        const fetchResearchTypesList = async () => {
            setLoadingResearchTypes(true);
            try {
                const db = await FirebaseApp();
                const researchRef = ref(db, "ResearchType");
                const snapshot = await get(researchRef);
                const types = [];
                snapshot.forEach(childSnapshot => {
                    types.push({
                        id: childSnapshot.key,
                        ...childSnapshot.val(),
                    });
                });
                setResearchTypes(types);
            } catch (err) {
                console.error("Error fetching research types:", err);
                toast.error("Unable to load research types.");
            } finally {
                setLoadingResearchTypes(false);
            }
        };

        fetchResearchTypesList();
    }, []);

    const fetchSalesManagers = () => {
        globalContext.setLoader(true);
        FirebaseApp().then(db => {
            const usersRef = ref(db, "SalesManager");

            return onValue(usersRef, (snapshot) => {
                const managers = [];
                snapshot.forEach(childSnapshot => {
                    managers.push({
                        id: childSnapshot.key,
                        email: childSnapshot.val(),
                        companies: []
                    });
                });

                managers.sort((a, b) => {
                    if (a.email === globalContext.userEmail) return -1;
                    if (b.email === globalContext.userEmail) return 1;
                    return 0;
                });

                setSalesManagers(managers);

                // Fetch companies for each manager
                managers.forEach(manager => {
                    fetchCompaniesForManager(manager.email);
                });

                globalContext.setLoader(false);
            }, (err) => {
                console.log('error --->', err);
                globalContext.setLoader(false);
            });
        }).catch(err => {
            console.log('error --->', err);
            globalContext.setLoader(false);
        });
    };

    const fetchCompaniesForManager = (managerEmail) => {
        FirebaseApp().then(db => {
            const companiesRef = ref(db, "CompanyKeys");

            return onValue(companiesRef, (snapshot) => {
                const companies = [];
                snapshot.forEach(childSnapshot => {
                    if (childSnapshot.val().created_by && childSnapshot.val().created_by === managerEmail) {
                        companies.push({ "company_key": childSnapshot.key, ...childSnapshot.val() });
                    }
                });

                setSalesManagers(prev =>
                    prev.map(manager =>
                        manager.email === managerEmail
                            ? { ...manager, companies }
                            : manager
                    )
                );
            });
        }).catch(err => {
            console.log('error fetching companies for manager --->', err);
        });
    };

    const fetchAllRelatedCompanies = () => {
        globalContext.setLoader(true);
        FirebaseApp().then(db => {
            const dbRef = ref(db, "CompanyKeys");
            return onValue(dbRef, (snapshot) => {
                const records = []
                snapshot.forEach(childSnapshot => {
                    if (childSnapshot.val().created_by && childSnapshot.val().created_by === globalContext.userEmail) {
                        records.push({ "company_key": childSnapshot.key, ...childSnapshot.val() });
                    }
                })
                setData(records);
                globalContext.setLoader(false);
            }, (err) => {
                console.log('error --->', err);
                globalContext.setLoader(false);
            });
        }).catch(err => {
            console.log('error --->', err);
            globalContext.setLoader(false);
        })
    }

    const filteredData = useMemo(() => {
        return data.filter(d =>
            d.company_name.toLowerCase().includes(search.toLowerCase()) ||
            d.company_key.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, data]);

    const filteredSalesManagers = useMemo(() => {
        return salesManagers.filter(manager =>
            manager.email.toLowerCase().includes(salesManagerSearch.toLowerCase())
        );
    }, [salesManagerSearch, salesManagers]);

    const getFilteredCompaniesForManager = (manager) => {
        const searchTerm = managerCompanySearches[manager.id] || '';
        return manager.companies.filter(company =>
            company.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            company.company_key.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const handleManagerCompanySearch = (managerId, searchTerm) => {
        setManagerCompanySearches(prev => ({
            ...prev,
            [managerId]: searchTerm
        }));
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setSelectedCompany({
            created_by: globalContext.userEmail,
            availablePrograms: ["all"],
            referral_discount: false,
            ...getDefaultResearchFields(),
            notification: getDefaultNotification(),
        });
        setInitialResearchType("");
        setSelectedPrograms([...PROGRAM_SELECTION_WITH_ALL]);
        setTotalLicenses(0);
    }

    const closeAddModal = () => {
        setAddModal(false);
        setSelectedCompany({
            created_by: globalContext.userEmail,
            availablePrograms: ["all"],
            referral_discount: false,
            ...getDefaultResearchFields(),
            notification: getDefaultNotification(),
        });
        setInitialResearchType("");
        setSelectedPrograms([...PROGRAM_SELECTION_WITH_ALL]);
    }

    const closeAddManager = () => {
        setAddManager(false);
        setSelectedSalesManager("");
    }

    const deriveProgramSelection = (availablePrograms = []) => {
        if (!availablePrograms.length || availablePrograms.includes("all")) {
            return [...PROGRAM_SELECTION_WITH_ALL];
        }

        return availablePrograms;
    };

    const editCompany = (company) => {
        setSelectedCompany({
            ...company,
            referral_discount: company.referral_discount ?? false,
            notification: company.notification || getDefaultNotification(),
            researchEnabled: company.researchEnabled ?? false,
            researchType: company.researchType || "",
            researchMinDaysSinceStart: company.researchMinDaysSinceStart ?? 0,
            researchMinCompletedSessions: company.researchMinCompletedSessions ?? 0,
            researchRequired: company.researchRequired ?? false,
            researchFollowUp: company.researchFollowUp ?? false,
            researchNotificationTime: company.researchNotificationTime || "",
        });
        setInitialResearchType(company.researchType || "");
        setTotalLicenses(company.total_licenses);
        setSelectedPrograms(deriveProgramSelection(company.availablePrograms || []));
        setShowEditModal(true);
    };

    const toggleStatus = (companyKey, status) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You want to update the status of this company key!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, update it!'
        }).then((result) => {
            if (result.isConfirmed) {
                FirebaseApp().then(db => {
                    const dbRef = ref(db, `CompanyKeys/${companyKey}`);
                    return get(dbRef).then((snapshot) => {
                        if (snapshot.exists()) {
                            const newStatus = !status;

                            return set(dbRef, { ...snapshot.val(), key_status: newStatus })
                                .then(() => {
                                    toast.success('Company Key Status Updated Successfully');
                                })
                                .catch(err => {
                                    console.error('Error updating Firebase:', err);
                                    toast.error('Error while updating company key status');
                                });

                        }
                    }).catch(err => {
                        console.log('error --->', err);
                        toast.error('Error While Updating Company Key Status');
                    });
                }).catch(err => {
                    console.log('error --->', err);
                    toast.error('Error While Updating Company Key Status');
                })
            }
        })
    };

    const updateCompanyLicenses = () => {
        const activeUsers = parseInt(selectedCompany.total_licenses) - parseInt(selectedCompany.available_licenses);
        const parsedTotal = parseInt(totalLicenses);

        if (selectedCompany.researchEnabled && !selectedCompany.researchType) {
            toast.error("Select a research type when research is enabled.");
            return;
        }

        if (isNaN(parsedTotal)) {
            toast.error("Enter a valid total license count");
            return;
        }

        if (parsedTotal < activeUsers) {
            toast.error("Total licenses should be greater or equal to active licenses");
            return;
        }

        const availableLicenses = parsedTotal - activeUsers;

        const researchPayload = buildResearchPayload();
        FirebaseApp().then(db => {
            const dbRef = ref(db, `CompanyKeys/${selectedCompany.company_key}`);
            return set(dbRef, {
                ...selectedCompany,
                company_name: selectedCompany.company_name,
                company_password: selectedCompany.company_password,
                total_licenses: parsedTotal,
                available_licenses: availableLicenses,
                availablePrograms: selectedCompany.availablePrograms || ["all"],
                ...researchPayload,
            })
                .then(() => {
                    toast.success('Company Key Updated Successfully');
                })
                .catch(err => {
                    console.error('Error updating Firebase:', err);
                    toast.error('Error while updating company key');
                });
            }).catch(err => {
                console.log('error --->', err);
                toast.error('Error While Updating Company Key');
            })
        closeEditModal();

    }

    const handleKeyChange = (val, key) => {
        setSelectedCompany({ ...selectedCompany, [key]: val });
    }

    const handleNotificationChange = (lang, field, value) => {
        setSelectedCompany(prev => {
            const previousNotification = prev.notification || getDefaultNotification();
            const previousLangValues = previousNotification[lang] || { title: "", description: "" };

            return {
                ...prev,
                notification: {
                    ...previousNotification,
                    [lang]: {
                        ...previousLangValues,
                        [field]: value,
                    },
                },
            };
        });
    };

    const handleProgramToggle = (program) => {
        setSelectedPrograms(prevSelection => {
            let nextSelection;
            if (program === "all") {
                const isAllSelected = prevSelection.includes("all");
                nextSelection = isAllSelected ? [] : [...PROGRAM_SELECTION_WITH_ALL];
            } else {
                const withoutAll = prevSelection.filter(p => p !== "all");
                const isSelected = withoutAll.includes(program);
                const updatedSelection = isSelected ? withoutAll.filter(p => p !== program) : [...withoutAll, program];
                nextSelection = updatedSelection.length === PROGRAM_LIST.length ? [...PROGRAM_SELECTION_WITH_ALL] : updatedSelection;
            }

            const availableProgramsForDb = nextSelection.includes("all") ? ["all"] : nextSelection;
            setSelectedCompany(prevCompany => ({ ...prevCompany, availablePrograms: availableProgramsForDb }));
            return nextSelection;
        });
    }

    const handleReferralDiscountToggle = () => {
        setSelectedCompany(prev => ({ ...prev, referral_discount: !prev.referral_discount }));
    };

    const toggleReferralDiscountStatus = (companyKey, currentValue) => {
        const nextValue = !currentValue;
        FirebaseApp().then(db => {
            const dbRef = ref(db, `CompanyKeys/${companyKey}`);
            return get(dbRef).then(snapshot => {
                if (!snapshot.exists()) {
                    toast.error("Company key not found.");
                    return;
                }

                return set(dbRef, { ...snapshot.val(), referral_discount: nextValue })
                    .then(() => {
                        setData(prev => prev.map(item =>
                            item.company_key === companyKey ? { ...item, referral_discount: nextValue } : item
                        ));
                        setSalesManagers(prev => prev.map(manager => ({
                            ...manager,
                            companies: manager.companies.map(company =>
                                company.company_key === companyKey ? { ...company, referral_discount: nextValue } : company
                            )
                        })));
                        toast.success("Referral discount updated.");
                    })
                    .catch(err => {
                        console.error("Error updating referral flag:", err);
                        toast.error("Unable to update referral discount.");
                    });
            });
        }).catch(err => {
            console.error('error toggling referral discount --->', err);
            toast.error("Unable to update referral discount.");
        });
    };

    const handleResearchToggle = () => {
        setSelectedCompany(prev => {
            const nextEnabled = !prev.researchEnabled;
            return {
                ...prev,
                researchEnabled: nextEnabled,
                researchType: nextEnabled ? (prev.researchType || getDefaultResearchType()) : prev.researchType,
                researchFollowUp: nextEnabled ? true : false,
                researchRequired: nextEnabled ? true : false
            };
        });
    };

    const handleResearchRequiredToggle = () => {
        setSelectedCompany(prev => ({ ...prev, researchRequired: !prev.researchRequired }));
    };

    const handleResearchFollowUpToggle = () => {
        setSelectedCompany(prev => ({ ...prev, researchFollowUp: !prev.researchFollowUp }));
    };

    const getDefaultResearchType = () => {
        return researchTypes.length ? researchTypes[0].id : "";
    };

    const buildResearchPayload = () => {
        const enabled = !!selectedCompany.researchEnabled;
        const followUp = enabled && !!selectedCompany.researchFollowUp;
        const parsedDays = followUp ? (parseInt(selectedCompany.researchMinDaysSinceStart) || 0) : 0;
        const parsedSessions = followUp ? (parseInt(selectedCompany.researchMinCompletedSessions) || 0) : 0;
        const notificationTime = followUp ? (selectedCompany.researchNotificationTime || "") : "";
        const nextResearchType = enabled ? (selectedCompany.researchType || getDefaultResearchType()) : "";
        const previousTimestamp = selectedCompany.researchTypeUpdatedAt;
        const shouldUpdateResearchTimestamp = nextResearchType && (
            nextResearchType !== initialResearchType || !previousTimestamp
        );
        let researchTypeUpdatedAt = previousTimestamp || null;

        if (nextResearchType && shouldUpdateResearchTimestamp) {
            researchTypeUpdatedAt = Date.now();
        }

        return {
            researchEnabled: enabled,
            researchType: nextResearchType,
            researchTypeUpdatedAt,
            researchMinDaysSinceStart: parsedDays,
            researchMinCompletedSessions: parsedSessions,
            researchRequired: enabled ? !!selectedCompany.researchRequired : false,
            researchFollowUp: followUp,
            researchNotificationTime: notificationTime,
        };
    };

    const renderNotificationSection = () => {
        const notification = selectedCompany.notification || getDefaultNotification();

        return (
            <>
                <div className="mb-3">
                    <strong>Notification Content</strong>
                </div>
                {NOTIFICATION_LANGUAGES.map(lang => (
                    <Row key={lang} className="g-3 mb-3">
                        <Col md={6}>
                            <Form.Group className="mb-0">
                                <Form.Label>{LANGUAGE_LABELS[lang] || lang.toUpperCase()} Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={notification[lang]?.title || ""}
                                    onChange={(e) => handleNotificationChange(lang, "title", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-0">
                                <Form.Label>{LANGUAGE_LABELS[lang] || lang.toUpperCase()} Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={notification[lang]?.description || ""}
                                    onChange={(e) => handleNotificationChange(lang, "description", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                ))}
            </>
        );
    };

    const renderResearchControls = () => {
        const enabled = !!selectedCompany.researchEnabled;
        const followUpEnabled = enabled && !!selectedCompany.researchFollowUp;

        return (
            <>
                <Form.Group className="mb-3">
                    <Form.Check
                        type="switch"
                        id="researchEnabled"
                        label="Research Enabled"
                        checked={enabled}
                        onChange={handleResearchToggle}
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Research Type</Form.Label>
                    <Form.Select
                        value={selectedCompany.researchType || ""}
                        onChange={(e) => handleKeyChange(e.target.value, "researchType")}
                        disabled={!enabled || (!researchTypes.length && !loadingResearchTypes)}
                    >
                        <option value="" disabled={researchTypes.length > 0}>
                            {loadingResearchTypes ? "Loading research types..." : "Select research type"}
                        </option>
                        {researchTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                                {type.title || type.id}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Check
                        type="switch"
                        id="researchFollowUp"
                        label="Research Follow-up"
                        checked={!!selectedCompany.researchFollowUp}
                        onChange={handleResearchFollowUpToggle}
                        disabled={!enabled}
                    />
                </Form.Group>
                {enabled && followUpEnabled && renderNotificationSection()}
                <Form.Group className="mb-3">
                    <Form.Label>Research Notification Time</Form.Label>
                    <Form.Control
                        type="time"
                        value={selectedCompany.researchNotificationTime || ""}
                        onChange={(e) => handleKeyChange(e.target.value, "researchNotificationTime")}
                        disabled={!followUpEnabled}
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Research Minimum Days Since Start</Form.Label>
                    <Form.Control
                        type="number"
                        min={0}
                        value={selectedCompany.researchMinDaysSinceStart || 0}
                        onChange={(e) => handleKeyChange(e.target.value, "researchMinDaysSinceStart")}
                        disabled={!followUpEnabled}
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Research Minimum Completed Sessions</Form.Label>
                    <Form.Control
                        type="number"
                        min={0}
                        value={selectedCompany.researchMinCompletedSessions || 0}
                        onChange={(e) => handleKeyChange(e.target.value, "researchMinCompletedSessions")}
                        disabled={!followUpEnabled}
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Check
                        type="switch"
                        id="researchRequired"
                        label="Research Required"
                        checked={!!selectedCompany.researchRequired}
                        onChange={handleResearchRequiredToggle}
                        disabled={!enabled}
                    />
                </Form.Group>
            </>
        );
    };

    const createSalesManager = () => {
        if (!selectedSalesManager) {
            toast.error("Please enter a sales manager email!");
            return;
        }

        FirebaseApp().then(db => {
            const usersRef = ref(db, "SalesManager");

            return get(usersRef).then((snapshot) => {
                const existingManagers = snapshot.val() || {};
                const managerEmails = Object.values(existingManagers);

                if (managerEmails.includes(selectedSalesManager)) {
                    toast.error("Sales Manager already exists!");
                    return;
                }

                const existingKeys = Object.keys(existingManagers).map(key => parseInt(key, 10)).filter(key => !isNaN(key));
                const largestKey = existingKeys.length ? Math.max(...existingKeys) : 0;
                const newManagerKey = largestKey + 1;
                const newManagerRef = ref(db, `SalesManager/${newManagerKey}`);

                return set(newManagerRef, selectedSalesManager)
                    .then(() => {
                        toast.success("Sales Manager added successfully!");
                        closeAddManager();
                    })
                    .catch(err => {
                        console.error("Error adding Sales Manager:", err);
                        toast.error("Error while adding Sales Manager!");
                    });
            }).catch(err => {
                console.error("Firebase error:", err);
                toast.error("Error connecting to Firebase!");
            });
        }).catch(err => {
            console.error("Firebase error:", err);
            toast.error("Error connecting to Firebase!");
        });
    };


    const createCompany = () => {
        if (!selectedCompany.company_key || !selectedCompany.company_name || !selectedCompany.company_password || !selectedCompany.total_licenses || selectedCompany.total_licenses <= 0 || !selectedCompany.created_by) {
            toast.error("All fields are required and licenses should be greater than 0!");
            return;
        }

        if (selectedCompany.researchEnabled && !selectedCompany.researchType) {
            toast.error("Select a research type when research is enabled.");
            return;
        }

        const researchPayload = buildResearchPayload();
        FirebaseApp().then(db => {
            const dbRef = ref(db, `CompanyKeys/${selectedCompany.company_key}`);

            return get(dbRef).then((snapshot) => {
                if (snapshot.exists()) {
                    toast.error("Company key already exists!");
                    return;
                }

                return set(dbRef, {
                    ...selectedCompany,
                    total_licenses: parseInt(selectedCompany.total_licenses),
                    available_licenses: parseInt(selectedCompany.total_licenses),
                    key_status: true,
                    ...researchPayload,
                }).then(() => {
                    toast.success("Company added successfully!");
                    closeAddModal();
                }).catch(err => {
                    console.error("Error adding company:", err);
                    toast.error("Error while adding company!");
                });
            }).catch(err => {
                console.error("Firebase error:", err);
                toast.error("Error connecting to Firebase!");
            });
    }).catch(err => {
            console.error("Firebase error:", err);
            toast.error("Error connecting to Firebase!");
        });
    }

    // Temporary helper to ensure ResearchType records exist.
    const createResearchTypeTable = () => {
        FirebaseApp().then(db => {
            const researchRef = ref(db, "ResearchType");
            const payload = RESEARCH_TYPES.reduce((acc, type) => {
                acc[type] = { title: type.toUpperCase() };
                return acc;
            }, {});

            return set(researchRef, payload)
                .then(() => {
                    toast.success("ResearchType table created in Firebase.");
                })
                .catch(err => {
                    console.error("Error creating ResearchType table:", err);
                    toast.error("Failed to create ResearchType table.");
                });
        }).catch(err => {
            console.error("Firebase error:", err);
            toast.error("Error connecting to Firebase!");
        });
    }

    // Temporary helper to ensure CompanyKeys records include some initial data.
    const addNewKeyToCompanyKeys = () => {
        FirebaseApp().then(db => {
            const companiesRef = ref(db, "CompanyKeys");
            return get(companiesRef).then((snapshot) => {
                const updates = [];

                snapshot.forEach(childSnapshot => {
                    const companyData = childSnapshot.val() || {};
                    const companyRef = ref(db, `CompanyKeys/${childSnapshot.key}`);
                    updates.push(set(companyRef, { ...companyData, notification: {
                        en: {
                            title: "",
                            description: ""
                        },
                        nl: {
                            title: "",
                            description: ""
                        },
                        he: {
                            title: "",
                            description: ""
                        }
                    } }));
                });

                if (!updates.length) {
                    toast.info("All company keys already include new key.");
                    return Promise.resolve();
                }

                return Promise.all(updates).then(() => {
                    toast.success("New key added to company keys.");
                });
            }).catch(err => {
                console.error("Error while adding new key:", err);
                toast.error("Error while updating company keys with new key.");
            });
        }).catch(err => {
            console.error("Firebase error:", err);
            toast.error("Error connecting to Firebase!");
        });
    };

    // Render Companies Table
    const renderViewModeFilter = () => (
        <InputGroup className="view-mode-filter">
            <InputGroup.Text>View Mode</InputGroup.Text>
            <Form.Select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                aria-label="Toggle companies table view mode"
            >
                <option value="basic">Basic</option>
                <option value="advanced">Advanced</option>
            </Form.Select>
        </InputGroup>
    );

    const renderCompaniesTable = (companiesToRender) => {
        const columnCount = viewMode === "advanced" ? 15 : 9;
        return (
            <div className="table-responsive">
                <Table striped bordered hover className="mb-0">
                    <thead>
                        <tr>
                            <th>Company Key</th>
                            <th>Company Name</th>
                            {viewMode === "advanced" && <th>Company Password</th>}
                            <th>Available Licenses</th>
                            <th>Total Licenses</th>
                            <th>Available Programs</th>
                            <th>Referral Discount</th>
                            <th>Research Type</th>
                            {viewMode === "advanced" && (
                                <>
                                    <th>Research Follow-up</th>
                                    <th>Research Notification Time</th>
                                    <th>Min Days Since Start</th>
                                    <th>Min Completed Sessions</th>
                                    <th>Research Required</th>
                                </>
                            )}
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {companiesToRender.length > 0 ?
                            companiesToRender.map((d) =>
                                <tr key={d.company_key}>
                                    <td>{d.company_key}</td>
                                    <td>{d.company_name}</td>
                                    {viewMode === "advanced" && <td>{d.company_password}</td>}
                                    <td>{d.available_licenses}</td>
                                    <td>{d.total_licenses}</td>
                                    <td>{Array.isArray(d.availablePrograms) ? capitalizeWords(d.availablePrograms.join(", ")) : (d.availablePrograms || "-")}</td>
                                    <td>
                                        <Form.Check
                                            type="switch"
                                            id={`referal-switch-${d.company_key}`}
                                            label=""
                                            aria-label="Toggle referral discount"
                                            checked={!!d.referral_discount}
                                            onChange={() => toggleReferralDiscountStatus(d.company_key, d.referral_discount)}
                                        />
                                    </td>
                                    <td>{d.researchType && d.researchEnabled ? d.researchType : "—"}</td>
                                    {viewMode === "advanced" && (
                                        <>
                                            <td>{d.researchFollowUp ? "Yes" : "No"}</td>
                                            <td>{d.researchNotificationTime || "—"}</td>
                                            <td>{d.researchMinDaysSinceStart ?? 0}</td>
                                            <td>{d.researchMinCompletedSessions ?? 0}</td>
                                            <td>{d.researchRequired ? "Yes" : "No"}</td>
                                        </>
                                    )}
                                    <td>
                                        <Form.Check
                                            type="switch"
                                            id={`switch-${d.company_key}`}
                                            checked={d.key_status}
                                            onChange={() => toggleStatus(d.company_key, d.key_status)}
                                        />
                                    </td>
                                    <td className="company-actions">
                                        <button
                                            type="button"
                                            className="action-icon primary-icon"
                                            title="View Dashboard"
                                            onClick={() => navigate(`/company/${d.company_key}`)}
                                        >
                                            <BsSpeedometer2 aria-hidden="true" />
                                        </button>
                                        <button
                                            type="button"
                                            className="action-icon primary-icon"
                                            title="View Devices"
                                            onClick={() => navigate(`/devices/${d.company_key}`)}
                                        >
                                            <BsPhone aria-hidden="true" />
                                        </button>
                                        <button
                                            type="button"
                                            className="action-icon secondary-icon"
                                            title="View Research Results"
                                            onClick={() => navigate(`/company/${d.company_key}/research-results`)}
                                        >
                                            <BsFileEarmarkBarGraph aria-hidden="true" />
                                        </button>
                                        <button
                                            type="button"
                                            className="action-icon secondary-icon"
                                            title="Edit Company Key"
                                            onClick={() => editCompany(d)}
                                        >
                                            <BsPencilSquare aria-hidden="true" />
                                        </button>
                                    </td>
                                </tr>
                            ) :
                            <tr>
                                <td colSpan={columnCount}>
                                    <h5 className="text-center">No data</h5>
                                </td>
                            </tr>
                        }
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <main id="main" className="main">
            <div className="pagetitle">
                <Row className="align-items-center mb-3">
                    <Col>
                        <h1>Companies</h1>
                    </Col>
                    <Col className="text-end">
                        {isSuperAdmin ? (
                            <div className="mt-3">
                                <Button onClick={() => addNewSalesManager()} className="me-2">Add Sales Manager</Button>
                                <Button onClick={() => navigate("/research")} className="me-2">Research</Button>
                                <Button onClick={() => addNewCompany()}>Add Company</Button>
                            </div>
                        ) : (
                            <div className="mt-3">
                                <Button onClick={() => addNewCompany()}>Add Company</Button>
                            </div>
                        )}
                    </Col>
                </Row>
                {isSuperAdmin ? (
                    <Row className="align-items-center">
                        <Col md={6}>
                            <div className="text-left mt-3">
                                <InputGroup>
                                    <FormControl
                                        className="search-field"
                                        placeholder="Search by Sales Manager Email"
                                        aria-label="Search"
                                        value={salesManagerSearch}
                                        onChange={(e) => setSalesManagerSearch(e.target.value)}
                                    />
                                </InputGroup>
                            </div>
                        </Col>
                        <Col md={6} className="text-md-end mt-3 mt-md-0">
                            <div className="d-flex justify-content-md-end mt-3">
                                {renderViewModeFilter()}
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <Row className="align-items-center">
                        <Col md={6}>
                            <div className="text-left mt-3">
                                <InputGroup>
                                    <FormControl
                                        className="search-field"
                                        placeholder="Search by Company Key or Company Name"
                                        aria-label="Search"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </InputGroup>
                            </div>
                        </Col>
                        <Col md={6} className="text-md-end mt-3 mt-md-0">
                            <div className="d-flex justify-content-md-end">
                                {renderViewModeFilter()}
                            </div>
                        </Col>
                    </Row>
                )}
            </div>
            <section className="section dashboard">
                {isSuperAdmin ? (
                    <Accordion activeKey={activeAccordionKey} onSelect={handleAccordionSelect}>
                        {filteredSalesManagers.length > 0 ? (
                            filteredSalesManagers.map((manager) => (
                                <Card key={manager.id}>
                                    <Accordion.Item eventKey={manager.id}>
                                        <Accordion.Header>
                                            {manager.name} ({manager.email}) - {manager.companies.length} Companies
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <div className="mt-3 mb-3">
                                                <InputGroup>
                                                    <FormControl
                                                        className="search-field"
                                                        placeholder="Search by Company Key or Company Name"
                                                        aria-label="Search"
                                                        value={managerCompanySearches[manager.id] || ''}
                                                        onChange={(e) => handleManagerCompanySearch(manager.id, e.target.value)}
                                                    />
                                                </InputGroup>
                                            </div>
                                            {renderCompaniesTable(getFilteredCompaniesForManager(manager))}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Card>
                            ))
                        ) : (
                            <Card className="text-center p-4">
                                <h5>No Sales Managers found</h5>
                            </Card>
                        )}
                    </Accordion>
                ) : (
                    renderCompaniesTable(filteredData)
                )}

                {/* Modal for editing company key */}
                <Modal show={showEditModal} onHide={closeEditModal} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Company Key</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Company Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={selectedCompany.company_name || ''}
                                    onChange={(e) => handleKeyChange(e.target.value, "company_name")}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Company Password</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={selectedCompany.company_password || ''}
                                    onChange={(e) => handleKeyChange(e.target.value, "company_password")}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Total Licenses</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={totalLicenses}
                                    onChange={(e) => setTotalLicenses(e.target.value)}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Available Programs</Form.Label>
                                <div className="d-flex flex-wrap gap-2">
                                    {PROGRAM_SELECTION_WITH_ALL.map(program => (
                                        <Form.Check
                                            inline
                                            key={program}
                                            type="checkbox"
                                            id={`program-${program}`}
                                            label={program === "all" ? "All" : capitalizeWords(program)}
                                            checked={selectedPrograms.includes(program)}
                                            onChange={() => handleProgramToggle(program)}
                                        />
                                    ))}
                                </div>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="referalDiscount-edit"
                                    label="Referral Discount"
                                    checked={!!selectedCompany.referral_discount}
                                    onChange={handleReferralDiscountToggle}
                                />
                            </Form.Group>
                            {renderResearchControls()}
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeEditModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={updateCompanyLicenses}>
                            Update
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal for adding new sales manager */}
                <Modal show={addManager} onHide={() => closeAddManager()}>
                    <Modal.Header closeButton>
                        <Modal.Title>Add New Sales Manager</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3 mt-2" controlId="exampleForm.ControlInput">
                                <Form.Label>Sales Manager Email</Form.Label>
                                <Form.Control type="email" value={selectedSalesManager} onChange={(e) => setSelectedSalesManager(e.target.value)} />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={createSalesManager}>
                            Add
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal for adding new company */}
                <Modal show={addModal} onHide={closeAddModal} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Add New Company</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Company Key</Form.Label>
                                <Form.Control type="text" value={selectedCompany.company_key || ''} onChange={(e) => handleKeyChange(e.target.value, "company_key")} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Company Name</Form.Label>
                                <Form.Control type="text" value={selectedCompany.company_name || ''} onChange={(e) => handleKeyChange(e.target.value, "company_name")} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Company Password</Form.Label>
                                <Form.Control type="text" value={selectedCompany.company_password || ''} onChange={(e) => handleKeyChange(e.target.value, "company_password")} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Total Licenses</Form.Label>
                                <Form.Control type="number" value={selectedCompany.total_licenses || ''} onChange={(e) => handleKeyChange(e.target.value, "total_licenses")} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Available Programs</Form.Label>
                                <div className="d-flex flex-wrap gap-2">
                                    {PROGRAM_SELECTION_WITH_ALL.map(program => (
                                        <Form.Check
                                            inline
                                            key={program}
                                            type="checkbox"
                                            id={`program-${program}`}
                                            label={program === "all" ? "All" : capitalizeWords(program)}
                                            checked={selectedPrograms.includes(program)}
                                            onChange={() => handleProgramToggle(program)}
                                        />
                                    ))}
                                </div>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="referalDiscount-add"
                                    label="Referral Discount"
                                    checked={!!selectedCompany.referral_discount}
                                    onChange={handleReferralDiscountToggle}
                                />
                            </Form.Group>
                            {renderResearchControls()}

                            {isSuperAdmin && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Assign To</Form.Label>
                                    <Form.Control type="email" value={selectedCompany.created_by || ''} onChange={(e) => handleKeyChange(e.target.value, "created_by")} />
                                </Form.Group>
                            )}
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={createCompany}>Add</Button>
                    </Modal.Footer>
                </Modal>
            </section>
        </main>
    )
}
